import React from 'react';
import Plot from 'react-plotly.js';
import axios from 'axios';

function RecurringManager({ assetId, isProperty=false, structures=[], land=null, initialSelectedName=null }) {
  const [includeChildren, setIncludeChildren] = React.useState(!!isProperty);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [series, setSeries] = React.useState([]); // [{name,type,last_frequency,asset_id}]
  const [filter, setFilter] = React.useState('');
  const [selected, setSelected] = React.useState(null); // { name, asset_id }

  // Use a ref to track if we've already tried to auto-select the initial name
  // so we don't keep overriding user selection if the component re-renders.
  const hasAutoSelected = React.useRef(false);

  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detail, setDetail] = React.useState([]); // expenses in series
  const [actionBusy, setActionBusy] = React.useState(false);
  const [detailView, setDetailView] = React.useState('graph'); // 'table' | 'graph' (default to Graph)
  // Graph controls
  const [period, setPeriod] = React.useState('monthly'); // 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually'
  const [cumulative, setCumulative] = React.useState(false);
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  // Table sorting state
  const [sortField, setSortField] = React.useState('date');
  const [sortDir, setSortDir] = React.useState('desc'); // 'asc' | 'desc'
  // Inline editing state for the selected series name
  const [editName, setEditName] = React.useState('');
  const [typeValue, setTypeValue] = React.useState('');
  const [freqValue, setFreqValue] = React.useState('');
  // Projection settings state
  const [showProjectionModal, setShowProjectionModal] = React.useState(false);
  const [isProjectable, setIsProjectable] = React.useState(false);
  const [projectionMethod, setProjectionMethod] = React.useState('last_actual');
  const [projectionLookback, setProjectionLookback] = React.useState(3);
  const [showProjected, setShowProjected] = React.useState(false);

  const labelForAsset = React.useCallback((aid)=>{
    try {
      if (!isProperty) return '';
      if (String(aid) === String(assetId)) return 'Property';
      if (land && String(land.id) === String(aid)) return 'Land';
      const idx = (structures||[]).findIndex(s => String(s.id) === String(aid));
      if (idx >= 0) return `${structures[idx].description || 'Structure'} (Structure ${idx+1})`;
      return `Asset ${aid}`;
    } catch (e) { return `Asset ${aid}`; }
  }, [assetId, isProperty, structures, land]);

  // Reset auto-selection flag if initialSelectedName changes
  React.useEffect(() => {
    hasAutoSelected.current = false;
  }, [initialSelectedName]);

  const loadSeries = React.useCallback(async ()=>{
    if (!assetId) return;
    setLoading(true); setError('');
    try {
      const url = includeChildren && isProperty
        ? `/api/balance_sheet/assets/physical_assets/${assetId}/recurring-series/?include_children=1&include_inactive=1`
        : `/api/balance_sheet/assets/physical_assets/${assetId}/recurring-series/?include_inactive=1`;
      const res = await axios.get(url);
      const items = Array.isArray(res.data) ? res.data : [];
      setSeries(items);
      // Ensure a selection exists: auto-select the initial name if provided,
      // otherwise fallback to the top-most item if nothing is selected
      // or if the previously selected item no longer exists in the refreshed list.
      if (items.length === 0) {
        if (selected) setSelected(null);
      } else {
        // 1. Try initialSelectedName first (whenever it matches an item and hasn't been auto-selected yet)
        if (initialSelectedName && !hasAutoSelected.current) {
          const found = items.find(s => s && s.name === initialSelectedName);
          if (found) {
            hasAutoSelected.current = true;
            setSelected({ name: found.name, asset_id: found.asset_id });
            return;
          }
        }

        // 2. Otherwise maintain existing or fallback
        const exists = selected && items.find(
          (s) => s && s.name === selected?.name && String(s.asset_id) === String(selected?.asset_id)
        );
        if (!exists) {
          const first = items[0];
          if (first && first.name) {
            setSelected({ name: first.name, asset_id: first.asset_id });
          } else {
            setSelected(null);
          }
        }
      }
    } catch (e) {
      setError('Failed to load recurring series.'); setSeries([]);
    } finally { setLoading(false); }
  }, [assetId, includeChildren, isProperty, selected, initialSelectedName]);

  React.useEffect(()=>{ loadSeries(); }, [loadSeries]);

  const loadDetail = React.useCallback(async (name)=>{
    if (!name) { setDetail([]); setProjectedData([]); return; }
    setDetailLoading(true);
    try {
      const url = includeChildren && isProperty
        ? `/api/balance_sheet/assets/physical_assets/${assetId}/recurring-series/manage/?name=${encodeURIComponent(name)}&include_children=1`
        : `/api/balance_sheet/assets/physical_assets/${assetId}/recurring-series/manage/?name=${encodeURIComponent(name)}`;
      const res = await axios.get(url);
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setDetail(items);
      if (res.data?.projection_settings) {
        setIsProjectable(res.data.projection_settings.is_projectable);
        setProjectionMethod(res.data.projection_settings.projection_method);
        setProjectionLookback(res.data.projection_settings.projection_lookback);
      }
      
    } catch (e) {
      setDetail([]);
      setProjectedData([]);
    } finally { setDetailLoading(false); }

    // Fetch projections separately — failure here must NOT clear detail
    try {
      const projUrl = includeChildren && isProperty
          ? `/api/balance_sheet/assets/physical_assets/${assetId}/linked-expenses/?include_children=1`
          : `/api/balance_sheet/assets/physical_assets/${assetId}/linked-expenses/`;
      const projRes = await axios.get(projUrl);
      const projItems = Array.isArray(projRes.data?.projected) ? projRes.data.projected : [];
      // Filter projections for the selected series
      setProjectedData(projItems.filter(p => p.recurring_name === name));
    } catch (e) {
      setProjectedData([]);
    }
  }, [assetId, includeChildren, isProperty]);

  const [projectedData, setProjectedData] = React.useState([]);

  React.useEffect(()=>{ if (selected?.name) loadDetail(selected.name); }, [selected?.name, loadDetail]);

  // When the selected series changes (user picks a different series), reset the edit fields.
  // We intentionally do NOT include `series` or `detail` as dependencies here — reloading
  // series/detail after an update must NOT overwrite values the user just saved.
  const prevSelectedNameRef = React.useRef(null);
  React.useEffect(() => {
    const selectedKey = selected ? `${selected.name}::${selected.asset_id}` : null;
    if (selectedKey === prevSelectedNameRef.current) return; // same series — don't reset
    prevSelectedNameRef.current = selectedKey;

    if (!selected) {
      setEditName('');
      setTypeValue('');
      setFreqValue('');
      return;
    }
    // Initialize editable title
    setEditName(selected.name || '');
    // Try to find the summary entry for this series to set type/freq
    try {
      const entry = (series || []).find(
        (s) => s && s.name === selected?.name && String(s.asset_id) === String(selected?.asset_id)
      );
      if (entry && (entry.type || entry.last_frequency)) {
        setTypeValue(entry.type || '');
        setFreqValue(entry.last_frequency || '');
        return;
      }
      // Fallback: use details (first row)
      if ((detail || []).length > 0) {
        const first = detail[0] || {};
        setTypeValue(first.recurring_type || '');
        setFreqValue(first.recurring_frequency || '');
      }
    } catch (e) {
      setTypeValue('');
      setFreqValue('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const doAction = async (body) => {
    if (!assetId || !selected?.name) return;
    setActionBusy(true);
    try {
      const payload = { include_children: includeChildren ? 1 : 0, ...body };
      await axios.post(`/api/balance_sheet/assets/physical_assets/${assetId}/recurring-series/manage/`, payload);
      // If we just renamed, update selection locally so detail reload uses the new name
      if (body?.action === 'rename' && body?.new_name && body?.old_name) {
        setSelected(prev => prev ? { ...prev, name: body.new_name } : prev);
        setEditName(body.new_name);
        await loadSeries();
        await loadDetail(body.new_name);
      } else {
        await loadSeries();
        await loadDetail(selected.name);
      }
    } catch (e) {}
    finally { setActionBusy(false); }
  };

  const filtered = React.useMemo(()=>{
    const q = String(filter||'').toLowerCase();
    if (!q) return series;
    return series.filter(s => {
      const lbl = labelForAsset(s.asset_id);
      return String(s.name||'').toLowerCase().includes(q)
        || String(s.type||'').toLowerCase().includes(q)
        || String(s.last_frequency||'').toLowerCase().includes(q)
        || String(s.expense_category||'').toLowerCase().includes(q)
        || String(lbl||'').toLowerCase().includes(q);
    });
  }, [series, filter, labelForAsset]);

  // Format expense category for display.
  // Rules:
  // - Only split into parent – child for known namespaced families (utilities_*).
  // - Otherwise, treat the whole string as a single label (underscores -> spaces).
  // - Handle variants that already use dashes/emdashes for utilities.
  const formatExpenseCategory = React.useCallback((raw) => {
    if (!raw) return '';
    try {
      const original = String(raw).trim();
      if (!original) return '';
      const lower = original.toLowerCase();

      // Normalize common "utilities child" formats
      // utilities_child (underscores)
      // utilities - child (dash)
      // utilities – child (en dash)
      // utilities — child (em dash)
      const UTIL = 'utilities';
      const dashChildMatch = lower.match(/^utilities\s*[-–—]\s*(.+)$/);
      if (dashChildMatch && dashChildMatch[1]) {
        let child = dashChildMatch[1].replace(/_/g, ' ').trim();
        child = child.replace(/\bgas electric\b/g, 'gas & electric');
        return `${UTIL} – ${child}`;
      }

      if (lower.startsWith('utilities_')) {
        let child = lower.slice('utilities_'.length).replace(/_/g, ' ').trim();
        child = child.replace(/\bgas electric\b/g, 'gas & electric');
        return `${UTIL} – ${child}`;
      }

      // For all other categories, do NOT split; just prettify underscores.
      return lower.replace(/_/g, ' ');
    } catch (e) {
      return String(raw).replace(/_/g, ' ');
    }
  }, []);

  // Helpers for graph aggregation
  const startOfWeekIso = (isoDate) => {
    // isoDate: 'YYYY-MM-DD' assumed UTC; return Monday as start of ISO week
    try {
      const [y, m, d] = isoDate.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      const day = dt.getUTCDay(); // 0..6 (Sun..Sat)
      const diff = (day === 0 ? -6 : 1 - day); // days to Monday
      const monday = new Date(dt);
      monday.setUTCDate(dt.getUTCDate() + diff);
      const y2 = monday.getUTCFullYear();
      const m2 = String(monday.getUTCMonth() + 1).padStart(2, '0');
      const d2 = String(monday.getUTCDate()).padStart(2, '0');
      return `${y2}-${m2}-${d2}`;
    } catch (e) {
      return isoDate;
    }
  };

  const quarterKey = (isoDate) => {
    try {
      const [y, m] = isoDate.split('-');
      const mi = Number(m);
      const q = mi <= 3 ? 1 : mi <= 6 ? 2 : mi <= 9 ? 3 : 4;
      return `${y}-Q${q}`;
    } catch (e) { return `${isoDate.slice(0,4)}-Q?`; }
  };

  const buildAggregatedSeries = React.useMemo(() => {
    // Returns { x:[], y:[], hoverText:[], projectedX:[], projectedY:[], projectedHover:[] }
    try {
      const buckets = new Map(); // key -> { total:number, txns:[] }
      const projectedBuckets = new Map(); // key -> { total:number, txns:[] }
      
      let minIso = startDate || null;
      let maxIso = endDate || null;
      
      const allData = [...(detail || [])];
      if (showProjected) {
          allData.push(...(projectedData || []));
      }

      for (const it of allData) {
        if (!it || !it.date) continue;
        const iso = String(it.date).slice(0, 10);
        if (startDate && iso < startDate) continue;
        if (endDate && iso > endDate) continue;

        if (!minIso || iso < minIso) minIso = iso;
        if (!maxIso || iso > maxIso) maxIso = iso;
        
        let key = iso; // daily default
        if (period === 'weekly') key = startOfWeekIso(iso);
        else if (period === 'monthly') key = iso.slice(0, 7);
        else if (period === 'quarterly') key = quarterKey(iso);
        else if (period === 'annually') key = iso.slice(0, 4);
        
        const amt = Math.abs(Number(it.amount) || 0);
        const isProj = it.source_expense_id !== undefined; // Projected items from backend have this

        const targetMap = isProj ? projectedBuckets : buckets;

        if (!targetMap.has(key)) targetMap.set(key, { total: 0, txns: [] });
        const bucket = targetMap.get(key);
        bucket.total += amt;
        bucket.txns.push({
          date: iso,
          amount: amt,
          description: it.description || (isProj ? 'Projected' : ''),
          asset_id: it.asset_id,
          expense_category: it.expense_category,
          isProjected: isProj,
        });
      }

      // Fill gaps for all periods so that spacing is visible even without transactions
      if (minIso && maxIso) {
        // Helper to add days to ISO date (UTC)
        const addDays = (isoStr, days) => {
          const [y,m,d] = isoStr.split('-').map(Number);
          const dt = new Date(Date.UTC(y, m-1, d));
          dt.setUTCDate(dt.getUTCDate() + days);
          const y2 = dt.getUTCFullYear();
          const m2 = String(dt.getUTCMonth()+1).padStart(2,'0');
          const d2 = String(dt.getUTCDate()).padStart(2,'0');
          return `${y2}-${m2}-${d2}`;
        };
        // Helper to add months to YYYY-MM
        const addMonths = (yearMonth, months) => {
          const [y, m] = yearMonth.split('-').map(Number);
          const dt = new Date(Date.UTC(y, m - 1 + months, 1));
          return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
        };
        // Helper to add quarters to YYYY-Qn
        const addQuarters = (yearQ, quarters) => {
          const [y, qpart] = yearQ.split('-Q');
          let yNum = Number(y);
          let qNum = Number(qpart);
          qNum += quarters;
          while (qNum > 4) { qNum -= 4; yNum += 1; }
          while (qNum < 1) { qNum += 4; yNum -= 1; }
          return `${yNum}-Q${qNum}`;
        };

        if (period === 'daily' || period === 'weekly') {
          let start = period === 'weekly' ? startOfWeekIso(minIso) : minIso;
          let end = period === 'weekly' ? startOfWeekIso(maxIso) : maxIso;
          const step = period === 'weekly' ? 7 : 1;
          let cur = start;
          while (cur <= end) {
            if (!buckets.has(cur)) buckets.set(cur, { total: 0, txns: [] });
            cur = addDays(cur, step);
          }
        } else if (period === 'monthly') {
          let cur = minIso.slice(0, 7);
          const end = maxIso.slice(0, 7);
          while (cur <= end) {
            if (!buckets.has(cur)) buckets.set(cur, { total: 0, txns: [] });
            cur = addMonths(cur, 1);
          }
        } else if (period === 'quarterly') {
          let cur = quarterKey(minIso);
          const end = quarterKey(maxIso);
          while (cur <= end || (cur.slice(0,4) === end.slice(0,4) && cur.slice(6) <= end.slice(6))) {
            if (!buckets.has(cur)) buckets.set(cur, { total: 0, txns: [] });
            if (cur === end) break;
            cur = addQuarters(cur, 1);
          }
        } else if (period === 'annually') {
          let cur = Number(minIso.slice(0, 4));
          const end = Number(maxIso.slice(0, 4));
          while (cur <= end) {
            const k = String(cur);
            if (!buckets.has(k)) buckets.set(k, { total: 0, txns: [] });
            cur += 1;
          }
        }
      }

      // Sort keys chronologically based on period
      const allKeys = Array.from(new Set([...buckets.keys(), ...projectedBuckets.keys()]));
      const keyOrderValue = (k) => {
        if (period === 'quarterly') {
          // k like 'YYYY-Qn' -> map to first month of quarter for ordering
          const [y, qpart] = k.split('-Q');
          const q = Number(qpart || 1);
          const month = (q - 1) * 3 + 1;
          return `${y}-${String(month).padStart(2,'0')}-01`;
        }
        if (period === 'annually') return `${k}-01-01`;
        if (period === 'monthly') return `${k}-01`;
        // daily or weekly already ISO date
        return k;
      };
      allKeys.sort((a,b) => keyOrderValue(a).localeCompare(keyOrderValue(b)));

      const x = [];
      const yRaw = [];
      const px = [];
      const pyRaw = [];
      for (const k of allKeys) {
        const { total } = buckets.get(k) || { total: 0 };
        const { total: pTotal } = projectedBuckets.get(k) || { total: 0 };
        x.push(k);
        yRaw.push(total);
        px.push(k);
        pyRaw.push(pTotal);
      }
      const yCumulative = yRaw.map((v, i) => yRaw.slice(0, i + 1).reduce((a, b) => a + b, 0));
      const pyCumulative = pyRaw.map((v, i) => {
          const actualToDate = yRaw.slice(0, i + 1).reduce((a, b) => a + b, 0);
          const projToDate = pyRaw.slice(0, i + 1).reduce((a, b) => a + b, 0);
          return actualToDate + projToDate;
      });
      const y = cumulative ? yCumulative : yRaw;
      const py = cumulative ? pyCumulative : pyRaw;

      // Stats calculation (now using aggregated period totals)
      const periodValues = yRaw;
      const sum = periodValues.reduce((a, b) => a + b, 0);
      const avg = periodValues.length > 0 ? sum / periodValues.length : 0;
      const stdDev = periodValues.length > 1
        ? Math.sqrt(periodValues.map(v => Math.pow(v - avg, 2)).reduce((a, b) => a + b, 0) / periodValues.length)
        : 0;

      // Build hovertexts now that we know both period total and cumulative
      const hoverText = allKeys.map((k, i) => {
        const bucket = buckets.get(k);
        if (!bucket) return null;
        const { total, txns } = bucket;
        const headerVal = cumulative ? yCumulative[i] : total;
        const headerLabel = cumulative ? 'Cumulative Total' : 'Total';
        const header = `<b>${k} (Actual)</b><br>${headerLabel}: $${(headerVal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const sortedTx = [...txns].sort((a,b)=> (b.date||'').localeCompare(a.date||'') || (b.amount - a.amount));
        const lines = sortedTx.map(t => {
          const pct = total > 0 ? Math.round((t.amount / total) * 1000) / 10 : 0; // one decimal, relative to period total
          const amtStr = `$${(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          return `<span style="font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;">${t.date}</span> \u00A0\u00A0 <span style="float:right; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;">${amtStr} (${pct}%)</span>`;
        });
        const body = lines.join('<br>');
        return `${header}<br><br>${body}`;
      });

      const phover = allKeys.map((k, i) => {
        const bucket = projectedBuckets.get(k);
        if (!bucket) return null;
        const { total, txns } = bucket;
        const headerVal = cumulative ? pyCumulative[i] : total;
        const headerLabel = cumulative ? 'Projected Cumulative' : 'Projected Total';
        const header = `<b>${k} (Projected)</b><br>${headerLabel}: $${(headerVal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const sortedTx = [...txns].sort((a,b)=> (b.date||'').localeCompare(a.date||'') || (b.amount - a.amount));
        const lines = sortedTx.map(t => {
          const pct = total > 0 ? Math.round((t.amount / total) * 1000) / 10 : 0;
          const amtStr = `$${(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          return `<span style="font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;">${t.date}</span> \u00A0\u00A0 <span style="float:right; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;">${amtStr} (${pct}%)</span>`;
        });
        const body = lines.join('<br>');
        return `${header}<br><br>${body}`;
      });

      return { x, y, hoverText, px, py, phover, stats: { sum, avg, stdDev } };
    } catch (e) {
      return { x: [], y: [], hoverText: [], px: [], py: [], phover: [], stats: { sum: 0, avg: 0, stdDev: 0 } };
    }
  }, [detail, period, cumulative, showProjected, projectedData, startDate, endDate]);

  // Filtered and Sorted details for Table view
  const sortedDetail = React.useMemo(() => {
    let arr = Array.isArray(detail) ? [...detail] : [];
    
    // Apply date filtering to table view as well
    if (startDate || endDate) {
      arr = arr.filter(it => {
        if (!it.date) return false;
        const iso = String(it.date).slice(0, 10);
        if (startDate && iso < startDate) return false;
        if (endDate && iso > endDate) return false;
        return true;
      });
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const getAssetLabel = (it) => labelForAsset(it.asset_id);
    arr.sort((a,b)=>{
      switch (sortField) {
        case 'date': {
          const da = String(a?.date || '');
          const db = String(b?.date || '');
          return dir * collator.compare(da, db);
        }
        case 'amount': {
          const aa = Math.abs(Number(a?.amount) || 0);
          const bb = Math.abs(Number(b?.amount) || 0);
          return dir * (aa - bb);
        }
        case 'asset': {
          const aa = getAssetLabel(a) || '';
          const bb = getAssetLabel(b) || '';
          return dir * collator.compare(aa, bb);
        }
        case 'subcategory': {
          const aa = String(a?.expense_category || '');
          const bb = String(b?.expense_category || '');
          return dir * collator.compare(aa, bb);
        }
        case 'description':
        default: {
          const aa = String(a?.description || '');
          const bb = String(b?.description || '');
          return dir * collator.compare(aa, bb);
        }
      }
    });
    return arr;
  }, [detail, sortField, sortDir, labelForAsset, startDate, endDate]);

  return (
    <div
      className="card"
      style={{
        marginTop: 16,
        background: '#0b1220',
        color: '#e5e7eb',
        border: '1px solid #374151',
      }}
    >
      <div
        className="card-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: '#111827',
          borderBottom: '1px solid #374151',
          color: '#e5e7eb',
        }}
      >
        <h3 style={{ margin: 0 }}>Recurring</h3>
        {isProperty && (
          <label
            className="form-check form-switch"
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <input
              className="form-check-input"
              type="checkbox"
              checked={includeChildren}
              onChange={(e) => setIncludeChildren(e.target.checked)}
              style={{ background: '#111827', borderColor: '#374151' }}
            />
            <span className="form-check-label" style={{ color: '#e5e7eb' }}>Include children</span>
          </label>
        )}
        {!isProperty && <div style={{ marginLeft: 'auto' }} />}
        <input
          className="form-control form-control-sm"
          placeholder="Search…"
          style={{
            width: 220,
            background: '#0b1220',
            color: '#e5e7eb',
            borderColor: '#374151',
          }}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      <div className="card-body" style={{ background: '#0b1220', color: '#e5e7eb' }}>
        <div className="row">
          <div className="col-md-5" style={{ borderRight: '1px solid #374151' }}>
            {loading ? (
              <div className="text-muted">Loading series…</div>
            ) : error ? (
              <div
                className="alert alert-warning"
                style={{ background: 'transparent', color: '#fbbf24', borderColor: '#7c5800' }}
              >
                {error}
              </div>
            ) : (
              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                {filtered.length === 0 && <div className="text-muted">No recurring series found.</div>}
                <ul className="list-group" style={{ background: 'transparent' }}>
                  {filtered.map((s, idx) => (
                    <li
                      key={`${s.name}-${idx}`}
                      className="list-group-item"
                      style={{
                        cursor: 'pointer',
                        background:
                          selected && selected?.name === s.name && String(selected?.asset_id) === String(s.asset_id)
                            ? '#1f2937'
                            : 'transparent',
                        color: '#e5e7eb',
                        borderColor: '#374151',
                      }}
                      onClick={() => setSelected({ name: s.name, asset_id: s.asset_id })}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {s.name}
                            {s.active === false && (
                              <span className="badge bg-secondary" style={{ marginLeft: 8, background: '#4b5563', color: '#e5e7eb' }}>Inactive</span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#9ca3af', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                            <span>{(s.type || '—')} • {(s.last_frequency || '—')}</span>
                            {s.expense_category ? (
                              <span style={{ color:'#cbd5e1' }}>• {formatExpenseCategory(s.expense_category)}</span>
                            ) : (
                              <span style={{ color:'#cbd5e1' }}>• —</span>
                            )}
                          </div>
                        </div>
                        {isProperty && (
                          <span className="badge bg-secondary" style={{ background: '#374151', color: '#e5e7eb' }}>
                            {labelForAsset(s.asset_id)}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="col-md-7">
            {!selected ? (
              <div className="text-muted">Select a series to view and manage its linked expenses.</div>
            ) : (
              <div>
                {/* Top controls row: Name, Type, Freq inline; Update/Stop; asset label on far right */}
                <div
                  className="d-flex"
                  style={{ alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}
                >
                  {/* Name */}
                  <div className="input-group input-group-sm" style={{ maxWidth: 380 }}>
                    <span className="input-group-text" style={{ background: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>Name</span>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const newName = (editName || '').trim();
                          if (!actionBusy && newName && newName !== selected.name) {
                            doAction({ action: 'rename', old_name: selected.name, new_name: newName });
                          }
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={() => {
                        const newName = (editName || '').trim();
                        if (!actionBusy && newName && newName !== selected.name) {
                          doAction({ action: 'rename', old_name: selected.name, new_name: newName });
                        } else if (!newName) {
                          setEditName(selected.name || '');
                        }
                      }}
                      disabled={actionBusy}
                      placeholder="Series name"
                      style={{
                        margin: 0,
                        background: '#0b1220',
                        color: '#e5e7eb',
                        borderColor: '#374151',
                        fontWeight: 600,
                      }}
                    />
                  </div>

                  {/* Type */}
                  <div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
                    <span className="input-group-text" style={{ background: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>Type</span>
                    <select
                      className="form-select"
                      value={typeValue}
                      onChange={(e) => setTypeValue(e.target.value)}
                      style={{ background: '#0b1220', color: '#e5e7eb', borderColor: '#374151' }}
                    >
                      <option value="">--</option>
                      <option value="fixed">Fixed</option>
                      <option value="variable">Variable</option>
                    </select>
                  </div>

                  {/* Freq */}
                  <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
                    <span className="input-group-text" style={{ background: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>Freq</span>
                    <select
                      className="form-select"
                      value={freqValue}
                      onChange={(e) => setFreqValue(e.target.value)}
                      style={{ background: '#0b1220', color: '#e5e7eb', borderColor: '#374151' }}
                    >
                      <option value="">--</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="bi_monthly">Bi-monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semi_annually">Semi-annually</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>

                  {/* Update */}
                  <button
                    className="btn btn-sm btn-secondary"
                    style={{ background: '#1f2937', color: '#e5e7eb', borderColor: '#374151' }}
                    disabled={actionBusy || (!typeValue && !freqValue)}
                    onClick={() =>
                      doAction({
                        action: 'update',
                        name: selected.name,
                        recurring_type: typeValue || null,
                        recurring_frequency: freqValue || null,
                      })
                    }
                  >
                    Update
                  </button>

                  {/* Stop */}
                  <button
                    className="btn btn-sm btn-outline-danger"
                    style={{ borderColor: '#7f1d1d', color: '#fca5a5' }}
                    disabled={actionBusy || (() => {
                      const entry = (series || []).find(
                        (s) => s && s.name === selected?.name && String(s.asset_id) === String(selected?.asset_id)
                      );
                      return entry && entry.active === false;
                    })()}
                    onClick={() => {
                      const msg = 'Stop this recurring series?\n\nThis will mark the series as inactive. You will not be able to link any additional incoming transactions to this series going forward.';
                      if (!window.confirm(msg)) return;
                      doAction({ action: 'stop', name: selected.name });
                    }}
                  >
                    Stop
                  </button>

                  <button
                    className="btn btn-sm btn-outline-info"
                    onClick={() => setShowProjectionModal(true)}
                    disabled={actionBusy}
                    title="Projection Settings"
                    style={{ background: 'transparent', borderColor: '#60a5fa', color: '#60a5fa' }}
                  >
                    Projection Settings
                  </button>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', userSelect: 'none', color: '#9ca3af', marginLeft: 12 }}>
                    <input
                      type="checkbox"
                      checked={showProjected}
                      onChange={(e) => setShowProjected(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    Show Projected Recurring
                  </label>

                  {/* Right-corner badges */}
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {(() => {
                      const entry = (series || []).find(
                        (s) => s && s.name === selected?.name && String(s.asset_id) === String(selected?.asset_id)
                      );
                      if (entry && entry.active === false) {
                        return (
                          <span className="badge bg-secondary" style={{ background: '#4b5563', color: '#e5e7eb' }}>Inactive</span>
                        );
                      }
                      return null;
                    })()}
                    {isProperty && (
                      <span className="badge bg-secondary" style={{ background: '#374151', color: '#e5e7eb' }}>
                        {labelForAsset(selected.asset_id)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ border: '1px solid #374151', borderRadius: 6 }}>
                  {/* Tabs header for Graph/Table */}
                  <div style={{
                    borderBottom: '1px solid #374151',
                    display: 'flex', alignItems: 'flex-end', gap: 4, padding: '6px 8px', background: '#0b1220'
                  }}>
                    {['graph','table'].map((mode) => {
                      const isActive = detailView === mode;
                      const label = mode === 'graph' ? 'Graph' : 'Table';
                      return (
                        <span
                          key={mode}
                          role="button"
                          tabIndex={0}
                          onClick={() => setDetailView(mode)}
                          onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') { e.preventDefault(); setDetailView(mode); } }}
                          style={{
                            cursor: 'pointer',
                            padding: '6px 10px',
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? '#2563eb' : '#9ca3af',
                            border: `1px solid ${isActive ? '#2563eb' : '#374151'}`,
                            borderBottom: isActive ? '1px solid transparent' : `1px solid #374151`,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            background: isActive ? '#0b1220' : 'transparent',
                            position: 'relative',
                            top: 1,
                            outline: 'none'
                          }}
                        >
                          {label}
                        </span>
                      );
                    })}
                  </div>

                  {/* Content area */}
                  <div style={{ padding: 8, background: '#0b1220' }}>
                    {detailLoading ? (
                      <div className="text-muted">Loading expenses…</div>
                    ) : detail.length === 0 ? (
                      <div className="text-muted">No expenses linked to this series.</div>
                    ) : detailView === 'graph' ? (
                      <div style={{ width: '100%', minHeight: 260 }}>
                        {/* Graph Controls */}
                        <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8 }}>
                          <div className="input-group input-group-sm" style={{ maxWidth: 240 }}>
                            <span className="input-group-text" style={{ background: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>Period</span>
                            <select
                              className="form-select"
                              value={period}
                              onChange={(e)=> setPeriod(e.target.value)}
                              style={{ background: '#0b1220', color: '#e5e7eb', borderColor: '#374151' }}
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                              <option value="quarterly">Quarterly</option>
                              <option value="annually">Annually</option>
                            </select>
                          </div>
                          <label className="form-check form-switch" style={{ marginLeft: 6 }}>
                            <input className="form-check-input" type="checkbox" checked={cumulative} onChange={e=> setCumulative(e.target.checked)} style={{ background: '#111827', borderColor: '#374151' }} />
                            <span className="form-check-label" style={{ color: '#e5e7eb', marginLeft: 6 }}>Cumulative</span>
                          </label>

                          {/* Date Pickers */}
                          <div className="d-flex align-items-center" style={{ gap: 4, marginLeft: 12 }}>
                            <div className="input-group input-group-sm" style={{ width: 'auto' }}>
                              <span className="input-group-text" style={{ background: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>From</span>
                              <input
                                type={period === 'monthly' ? 'month' : (period === 'annually' ? 'number' : 'date')}
                                className="form-control"
                                style={{ background: '#0b1220', color: '#e5e7eb', borderColor: '#374151', width: period === 'annually' ? 80 : 'auto' }}
                                value={period === 'annually' ? startDate.slice(0, 4) : (period === 'monthly' ? startDate.slice(0, 7) : startDate)}
                                onChange={(e) => {
                                  let val = e.target.value;
                                  if (period === 'annually' && val) val = `${val}-01-01`;
                                  if (period === 'monthly' && val) val = `${val}-01`;
                                  setStartDate(val);
                                }}
                                placeholder={period === 'annually' ? 'YYYY' : ''}
                              />
                            </div>
                            <div className="input-group input-group-sm" style={{ width: 'auto' }}>
                              <span className="input-group-text" style={{ background: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>To</span>
                              <input
                                type={period === 'monthly' ? 'month' : (period === 'annually' ? 'number' : 'date')}
                                className="form-control"
                                style={{ background: '#0b1220', color: '#e5e7eb', borderColor: '#374151', width: period === 'annually' ? 80 : 'auto' }}
                                value={period === 'annually' ? endDate.slice(0, 4) : (period === 'monthly' ? endDate.slice(0, 7) : endDate)}
                                onChange={(e) => {
                                  let val = e.target.value;
                                  if (period === 'annually' && val) val = `${val}-12-31`;
                                  if (period === 'monthly' && val) {
                                    const [y, m] = val.split('-');
                                    const lastDay = new Date(Date.UTC(Number(y), Number(m), 0)).getUTCDate();
                                    val = `${val}-${String(lastDay).padStart(2, '0')}`;
                                  }
                                  setEndDate(val);
                                }}
                                placeholder={period === 'annually' ? 'YYYY' : ''}
                              />
                            </div>
                            {(startDate || endDate) && (
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => { setStartDate(''); setEndDate(''); }}
                                style={{ padding: '2px 8px', borderColor: '#374151', color: '#9ca3af' }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                          {buildAggregatedSeries.stats && (
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, color: '#9ca3af', fontSize: '13px' }}>
                              <span>Sum: <strong style={{ color: '#e5e7eb' }}>${buildAggregatedSeries.stats.sum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                              <span>Avg: <strong style={{ color: '#e5e7eb' }}>${buildAggregatedSeries.stats.avg.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                              <span>Std Dev: <strong style={{ color: '#e5e7eb' }}>${buildAggregatedSeries.stats.stdDev.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
                            </div>
                          )}
                        </div>
                        <Plot
                          data={[
                            {
                              type: 'bar',
                              x: buildAggregatedSeries.x,
                              y: buildAggregatedSeries.y,
                              marker: { color: '#60a5fa' },
                              hovertext: buildAggregatedSeries.hoverText,
                              hovertemplate: '%{hovertext}<extra></extra>',
                              name: 'Actual'
                            },
                            ...(showProjected ? [{
                              type: 'bar',
                              x: buildAggregatedSeries.px,
                              y: buildAggregatedSeries.py,
                              marker: { color: '#60a5fa', opacity: 0.5 },
                              hovertext: buildAggregatedSeries.phover,
                              hovertemplate: '%{hovertext}<extra></extra>',
                              name: 'Projected'
                            }] : [])
                          ]}
                          layout={{
                            paper_bgcolor: '#0b1220',
                            plot_bgcolor: '#0b1220',
                            barmode: cumulative ? 'group' : 'stack',
                            showlegend: showProjected,
                            legend: { font: { color: '#cbd5e1' }, orientation: 'h', y: 1.1 },
                            margin: { l: 50, r: 20, t: 30, b: 40 },
                            xaxis: (() => {
                              const base = {
                                title: period.charAt(0).toUpperCase() + period.slice(1),
                                color: '#cbd5e1',
                                gridcolor: '#1f2937',
                              };
                              if (period === 'daily') {
                                return {
                                  ...base,
                                  type: 'date',
                                  tickmode: 'linear',
                                  dtick: 'D1', // show tick for every day
                                  tickformat: '%b %-d',
                                  tickangle: -45,
                                };
                              }
                              if (period === 'weekly') {
                                return {
                                  ...base,
                                  type: 'date',
                                  tickmode: 'linear',
                                  dtick: 604800000, // 7 days in ms
                                  tickformat: '%b %-d',
                                  tickangle: -45,
                                };
                              }
                              return {
                                ...base,
                                type: 'category',
                              };
                            })(),
                            yaxis: {
                              title: cumulative ? 'USD (Cumulative)' : 'USD',
                              color: '#cbd5e1',
                              gridcolor: '#1f2937',
                              tickprefix: '$',
                              rangemode: 'tozero',
                              separatethousands: true,
                            },
                            showlegend: false,
                            autosize: true,
                          }}
                          config={{ displayModeBar: false, responsive: true }}
                          useResizeHandler={true}
                          style={{ width: '100%', height: '320px' }}
                        />
                      </div>
                    ) : (
                      <div className="table-responsive" style={{ maxHeight: 360, overflowY: 'auto', background: '#0b1220' }}>
                        <table
                          className="table table-sm"
                          style={{
                            margin: 0,
                            color: '#e5e7eb',
                            background: '#0b1220',
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            '--bs-table-bg': '#0b1220',
                            '--bs-table-color': '#e5e7eb',
                            '--bs-table-striped-bg': '#0f172a',
                            '--bs-table-hover-bg': '#1f2937',
                            '--bs-table-border-color': '#374151',
                            '--bs-border-color': '#374151',
                          }}
                        >
                          <thead style={{ position: 'sticky', top: 0, background: '#111827', color: '#e5e7eb' }}>
                            <TableHeader
                              isProperty={isProperty}
                              onSortChange={(f,d)=>{ setSortField(f); setSortDir(d); }}
                              sortField={sortField}
                              sortDir={sortDir}
                            />
                          </thead>
                          <tbody style={{ borderColor: '#374151', background: '#0b1220', color: '#e5e7eb' }}>
                            {sortedDetail.map((it, i) => (
                              <tr key={it.id || i} style={{ background: '#0b1220', color: '#e5e7eb' }}>
                                <td style={{ borderColor: '#374151', background: '#0b1220', color: '#e5e7eb' }}>{it.date || ''}</td>
                                <td style={{ borderColor: '#374151', background: '#0b1220', color: '#e5e7eb' }}>
                                  {(Number(it.amount) || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                                </td>
                                {isProperty && (
                                  <td style={{ borderColor: '#374151', background: '#0b1220', color: '#e5e7eb' }}>
                                    {labelForAsset(it.asset_id)}
                                  </td>
                                )}
                                <td style={{ borderColor: '#374151', background: '#0b1220', color: '#e5e7eb' }}>
                                  {String(it.expense_category || '').replace(/_/g, ' ') || '—'}
                                </td>
                                <td style={{ borderColor: '#374151', background: '#0b1220', color: '#e5e7eb' }}>{it.description || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showProjectionModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
          <div style={{ width:'min(450px, 95vw)', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb', padding: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Projection Settings: {selected?.name}</div>
            
            <div className="mb-3">
              <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isProjectable}
                  onChange={(e) => setIsProjectable(e.target.checked)}
                />
                Enable Projections
              </label>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                If enabled, the system will forecast future expenses for this series.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label" style={{ fontSize: 14 }}>Projection Method</label>
              <select
                className="form-select form-select-sm"
                style={{ background: '#111827', color: '#e5e7eb', borderColor: '#374151' }}
                value={projectionMethod}
                onChange={(e) => setProjectionMethod(e.target.value)}
                disabled={!isProjectable}
              >
                <option value="last_actual">Last Actual (Static)</option>
                <option value="prior_year">Prior Year (Seasonal)</option>
                <option value="moving_average">Moving Average</option>
              </select>
            </div>

            {projectionMethod === 'moving_average' && (
              <div className="mb-3">
                <label className="form-label" style={{ fontSize: 14 }}>Moving Average Lookback (Months)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  style={{ background: '#111827', color: '#e5e7eb', borderColor: '#374151' }}
                  value={projectionLookback}
                  onChange={(e) => setProjectionLookback(parseInt(e.target.value) || 0)}
                  min="1"
                  max="24"
                  disabled={!isProjectable}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowProjectionModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={async () => {
                    await doAction({
                        action: 'update_projections',
                        name: selected.name,
                        is_projectable: isProjectable,
                        projection_method: projectionMethod,
                        projection_lookback: projectionLookback
                    });
                    setShowProjectionModal(false);
                }}
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TableHeader({ isProperty, sortField, sortDir, onSortChange }) {
  const Col = ({ id, label }) => {
    const active = sortField === id;
    const dir = active ? sortDir : undefined;
    const indicator = active ? (dir === 'asc' ? '▲' : '▼') : '';
    const nextDir = active && dir === 'asc' ? 'desc' : 'asc';
    const onClick = () => onSortChange(id, nextDir);
    return (
      <th
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') { e.preventDefault(); onClick(); } }}
        style={{ cursor:'pointer', userSelect:'none', borderColor: '#374151', background: '#111827', color: '#e5e7eb', whiteSpace:'nowrap' }}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span>{label}</span>
        <span style={{ marginLeft: 6, color: '#9ca3af' }}>{indicator}</span>
      </th>
    );
  };
  return (
    <tr>
      <Col id="date" label="Date" />
      <Col id="amount" label="Amount" />
      {isProperty && (
        <Col id="asset" label="Asset" />
      )}
      <Col id="subcategory" label="Sub-Category" />
      <Col id="description" label="Description" />
    </tr>
  );
}

export default RecurringManager;
